import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  ChangeDetectorRef
} from '@angular/core';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { ExternalAppService, ExternalApp, LitWidgetHost, GridBridge } from 'src/app/services/external-app.service';
import { WidgetService } from 'src/app/services/widget.service';
import { KeycloakService } from 'keycloak-angular';

/**
 * Widget that hosts external web component apps.
 *
 * This is the bridge between lit-server's widget system and customer-provided
 * web components. It:
 * 1. Loads the app's scripts/styles
 * 2. Creates the web component element
 * 3. Injects the LitWidgetHost interface
 * 4. Handles cleanup on destroy
 */
@Component({
  selector: 'app-external-app-widget',
  templateUrl: './external-app-widget.component.html',
  styleUrls: ['../base-widget/base-widget.component.scss', './external-app-widget.component.scss'],
  standalone: false
})
export class ExternalAppWidgetComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  // The external app to load
  @Input() app: ExternalApp = null;

  // Alternative: specify by app ID (e.g., 'team:reddit')
  @Input() appId: string = null;

  // Configuration to pass to the app
  @Input() config: Record<string, any> = {};

  // Base path override (if not using app's default)
  @Input() basePath: string = null;

  @ViewChild('appContainer', { static: true }) appContainer: ElementRef<HTMLDivElement>;

  loading = true;
  error: string | null = null;
  private host: LitWidgetHost = null;
  private appElement: HTMLElement = null;

  constructor(
    private externalAppService: ExternalAppService,
    private cdr: ChangeDetectorRef,
    private widgetService: WidgetService,
    private keycloak: KeycloakService,
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    try {
      // Wait for auth to be ready (token in localStorage) before loading
      // This prevents 401 errors on page refresh when widget loads before auth completes
      await this.waitForAuth();

      // Scripts don't survive page refresh — the serialized app.loaded flag is stale.
      if (this.app) {
        this.app.loaded = false;
      }

      // Resolve app if only ID provided
      if (!this.app && this.appId) {
        const apps = await this.externalAppService.discoverApps(this.teamName);
        this.app = apps.find(a => a.id === this.appId);
        if (!this.app) {
          throw new Error(`App not found: ${this.appId}`);
        }
      }

      if (!this.app) {
        throw new Error('No app specified');
      }

      // Load app scripts/styles
      await this.externalAppService.loadApp(this.app);

      // Create the host interface
      const effectiveBasePath = this.basePath || this.app.basePath || this.app.manifest.config?.basePath;
      const gridPage = this.widgetService.grid;
      const keycloak = this.keycloak;
      const grid: GridBridge | undefined = gridPage ? {
        openFile: (path) => gridPage.$editFile({ parentId: null, path }),
        openMarkdown: (path, title) => gridPage.$viewMarkdown({ parentId: null, path, title }),
        openCsv: (path, title) => gridPage.$viewCsv({ parentId: null, path, title }),
        previewFile: (path, type) => gridPage.$previewFile({ parentId: null, path, previewType: type }),
        openWorkSession: (params) => gridPage.$openWorkSession(params),
        setBadge: (count) => gridPage.$setPanelBadge(this.id, count),
        focusSelf: () => gridPage.$focusPanel(this.id),
      } : undefined;
      this.host = this.externalAppService.createHost(
        this.id,
        this.teamName,
        effectiveBasePath,
        {
          getToken: async () => {
            try {
              await keycloak.updateToken(30);
              return await keycloak.getToken() || '';
            } catch { return ''; }
          },
          grid,
          appName: this.app?.manifest?.name || undefined,
        }
      );

      // Create the web component element
      this.appElement = document.createElement(this.app.manifest.element);

      // Inject the host interface
      (this.appElement as any).litWidgetHost = this.host;

      // Pass configuration as attributes
      const mergedConfig = { ...this.app.manifest.config, ...this.config };
      for (const [key, value] of Object.entries(mergedConfig)) {
        if (typeof value === 'string') {
          this.appElement.setAttribute(key, value);
        } else {
          this.appElement.setAttribute(key, JSON.stringify(value));
        }
      }

      // Set base-path attribute
      this.appElement.setAttribute('base-path', effectiveBasePath);

      // Append to container
      this.appContainer.nativeElement.appendChild(this.appElement);

      this.loading = false;
      this.cdr.detectChanges();

    } catch (err: any) {
      this.error = err.message || 'Failed to load app';
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    // Clean up host
    if (this.host) {
      this.externalAppService.destroyHost(this.id);
    }

    // Remove app element
    if (this.appElement && this.appElement.parentNode) {
      this.appElement.parentNode.removeChild(this.appElement);
    }

    super.ngOnDestroy();
  }

  get title(): string {
    return this.app?.manifest?.title || 'App';
  }

  /**
   * Wait for auth token to be available in localStorage.
   * On page refresh, widgets may load before keycloak auth completes.
   */
  private waitForAuth(maxAttempts = 50, intervalMs = 100): Promise<void> {
    return new Promise((resolve) => {
      // If token already exists, resolve immediately
      if (localStorage.getItem('id_token')) {
        resolve();
        return;
      }

      let attempts = 0;
      const check = setInterval(() => {
        attempts++;
        if (localStorage.getItem('id_token') || attempts >= maxAttempts) {
          clearInterval(check);
          resolve();
        }
      }, intervalMs);
    });
  }
}
